import apiClient from './apiClient';

const adminService = {

    // ============ GESTIÓN DE USUARIOS (SOLO SUPERADMIN) ============


    /**
 * Obtiene todos los administradores con sus parqueos asignados
 * Requiere rol de SuperAdmin
 */
    getAllAdmins: async () => {
        try {
            const response = await apiClient.get('/admin/getAllAdmins');
            console.log(response.data.admins);
            return response.data;
        } catch (error) {
            console.error('Error al obtener administradores:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Obtiene los parqueos disponibles para asignar
     * Requiere rol de SuperAdmin
     */
    getAvailableParkings: async () => {
        try {
            const response = await apiClient.get('/admin/getAvailableParkings');
            return response.data;
        } catch (error) {
            console.error('Error al obtener parqueos disponibles:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Asigna parqueos a un administrador
     * Requiere rol de SuperAdmin
     * @param {string} adminId - ID del administrador
     * @param {string[]} parkingIds - Array de IDs de parqueos a asignar
     */
    assignParkingsToAdmin: async (adminId, parkingIds) => {
        console.log("La prueba de frontend:", adminId, parkingIds);
        try {
            const response = await apiClient.post('/admin/assignParkingsToAdmin', {
                adminId,
                parkingIds
            });
            return response.data;
        } catch (error) {
            console.error('Error al asignar parqueos:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Desasigna parqueos específicos de un administrador
     * Requiere rol de SuperAdmin
     * @param {string} adminId - ID del administrador
     * @param {string[]} parkingIds - Array de IDs de parqueos a desasignar
     */
    unassignParkingsFromAdmin: async (adminId, parkingIds) => {
        try {
            const response = await apiClient.post('/admin/unassignParkingsFromAdmin', {
                adminId,
                parkingIds
            });
            return response.data;
        } catch (error) {
            console.error('Error al desasignar parqueos:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Crea un nuevo administrador con parqueos asignados
     * Requiere rol de SuperAdmin
     * @param {object} userData - Datos del usuario a convertir en admin
     * @param {string[]} parkingIds - Array de IDs de parqueos a asignar (opcional)
     */
    createAdminWithParkings: async (userData, parkingIds = []) => {
        try {
            const response = await apiClient.post('/admin/createAdminWithParkings', {
                userData,
                parkingIds
            });
            return response.data;
        } catch (error) {
            console.error('Error al crear administrador:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Elimina un administrador (lo convierte de vuelta a UserApp)
     * Requiere rol de SuperAdmin
     * @param {string} adminId - ID del administrador a eliminar
     */
    removeAdmin: async (adminId) => {
        try {
            const response = await apiClient.delete(`/admin/removeAdmin/${adminId}`);
            return response.data;
        } catch (error) {
            console.error('Error al eliminar administrador:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Obtiene todos los usuarios.
     * Requiere rol de SuperAdmin.
     */
    getAllUsers: async () => {
        try {
            const response = await apiClient.get('/admin/getAllUsers');
            return response.data;
        } catch (error) {
            console.error('Error al obtener todos los usuarios:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Obtiene usuarios filtrados por rol.
     * Requiere rol de SuperAdmin.
     * @param {string} role - El rol por el cual filtrar (e.g., 'Admin', 'User').
     */
    getUsersByRole: async (role) => {
        try {
            const response = await apiClient.get('/admin/getUsersByRole', { params: { role } });
            return response.data;
        } catch (error) {
            console.error(`Error al obtener usuarios por rol ${role}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Edita un usuario por su ID.
     * Requiere rol de SuperAdmin.
     * @param {string} idUser - El ID del usuario a editar.
     * @param {object} updateData - Los datos para actualizar.
     */
    userEdit: async (idUser, updateData) => {
        try {
            const response = await apiClient.put(`/admin/userEdit/${idUser}`, updateData);
            return response.data;
        } catch (error) {
            console.error(`Error al editar el usuario ${idUser}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Elimina un usuario por su cédula.
     * Requiere rol de SuperAdmin.
     * @param {string} idCard - La cédula del usuario a eliminar.
     */
    deleteUser: async (idCard) => {
        try {
            const response = await apiClient.delete(`/admin/deleteUser/${idCard}`);
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar el usuario ${idCard}:`, error.response?.data || error.message);
            throw error;
        }
    },

    // ============ GESTIÓN DE SUSCRIPCIONES ============

    /**
     * Crea una nueva suscripción.
     * Requiere rol de SuperAdmin.
     * @param {object} subscriptionData - Los datos de la nueva suscripción.
     */
    createSub: async (subscriptionData) => {
        try {
            const response = await apiClient.post('/admin/createSub', subscriptionData);
            return response.data;
        } catch (error) {
            console.error('Error al crear la suscripción:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Edita una suscripción existente.
     * Requiere rol de SuperAdmin.
     * @param {string} subId - El ID de la suscripción a editar.
     * @param {object} subscriptionData - Los datos para actualizar.
     */
    editSub: async (subId, subscriptionData) => {
        try {
            const response = await apiClient.put(`/admin/editSub/${subId}`, subscriptionData);
            return response.data;
        } catch (error) {
            console.error(`Error al editar la suscripción ${subId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Obtiene todas las suscripciones (planes).
     * Requiere rol de SuperAdmin.
     */
    getAllSubscriptions: async () => {
        try {
            const response = await apiClient.get('/admin/getAllSubscriptions');
            return response.data;
        } catch (error) {
            console.error('Error al obtener todas las suscripciones:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Elimina una suscripción.
     * Requiere rol de SuperAdmin.
     * @param {string} subId - El ID de la suscripción a eliminar.
     */
    deleteSub: async (subId) => {
        try {
            const response = await apiClient.delete(`/admin/deleteSub/${subId}`);
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar la suscripción ${subId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Obtiene todas las suscripciones de los usuarios.
     * Requiere rol de SuperAdmin.
     */
    getAllUserSubscriptions: async () => {
        try {
            const response = await apiClient.get('/admin/getAllUserSubscriptions');
            return response.data;
        } catch (error) {
            console.error('Error al obtener las suscripciones de usuarios:', error.response?.data || error.message);
            throw error;
        }
    },


    // ============ GESTIÓN DE PARQUEOS ============

    /**
     * Obtiene los parqueos según los permisos del admin (todos para SuperAdmin, asignados para Admin).
     */
    getAdminParkings: async () => {
        try {
            const response = await apiClient.get('/admin/getAdminParkings');
            return response.data;
        } catch (error) {
            console.error('Error al obtener parqueos del admin:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Crea un nuevo parqueo.
     * Requiere rol de SuperAdmin.
     * @param {object} parkingData - Los datos del nuevo parqueo.
     */
    createParking: async (parkingData) => {
        try {
            const response = await apiClient.post('/admin/createParking', parkingData);
            return response.data;
        } catch (error) {
            console.error('Error al crear el parqueo:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Edita un parqueo existente.
     * @param {string} id - El ID del parqueo a editar.
     * @param {object} parkingData - Los datos para actualizar.
     */
    editParking: async (id, parkingData) => {
        try {
            const response = await apiClient.put(`/admin/editParking/${id}`, parkingData);
            return response.data;
        } catch (error) {
            console.error(`Error al editar el parqueo ${id}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Elimina un parqueo.
     * Requiere rol de SuperAdmin.
     * @param {string} id - El ID del parqueo a eliminar.
     */
    deleteParking: async (id) => {
        try {
            const response = await apiClient.delete(`/admin/deleteParking/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar el parqueo ${id}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Actualiza la tarifa de un parqueo.
     * @param {string} parkingId - El ID del parqueo.
     * @param {number} tarify - La nueva tarifa.
     */
    updateParkingTariff: async (parkingId, tarify) => {
        try {
            const response = await apiClient.put(`/admin/updateParkingTariff/${parkingId}`, { tarify });
            return response.data;
        } catch (error) {
            console.error(`Error al actualizar la tarifa del parqueo ${parkingId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    // ============ GESTIÓN DE ESPACIOS (SPOTS) ============

    /**
     * Obtiene todos los espacios según permisos del admin.
     */
    getAllSpots: async () => {
        try {
            const response = await apiClient.get('/admin/getAllSpots');
            return response.data;
        } catch (error) {
            console.error('Error al obtener los espacios:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Crea un nuevo espacio en un parqueo.
     * @param {object} spotData - Datos del espacio, debe incluir 'parkingId'.
     */
    createSpot: async (spotData) => {
        try {
            const response = await apiClient.post('/admin/createSpot', spotData);
            return response.data;
        } catch (error) {
            console.error('Error al crear el espacio:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Edita un espacio existente.
     * @param {object} spotData - Datos del espacio, debe incluir 'spotId'.
     */
    editSpot: async (spotData) => {
        try {
            const response = await apiClient.put('/admin/editSpot', spotData);
            return response.data;
        } catch (error) {
            console.error(`Error al editar el espacio ${spotData.spotId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Elimina un espacio.
     * @param {string} spotId - El ID del espacio a eliminar.
     */
    deleteSpot: async (spotId) => {
        try {
            const response = await apiClient.delete('/admin/deleteSpot', { params: { spotId } });
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar el espacio ${spotId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    // ============ GESTIÓN DE RESERVAS ============

    /**
     * Obtiene todas las reservas según permisos del admin.
     */


    getAllReservations: async () => {
        try {
            console.log('Iniciando llamada a getAllReservations...');

            // Verificar que tenemos token antes de hacer la llamada
            const token = sessionStorage.getItem('token') || localStorage.getItem('token');
            if (!token) {
                throw new Error('No hay token de autenticación disponible');
            }

            // Verificar que el usuario actual tiene rol de admin


            console.log('Haciendo llamada al backend...');
            const response = await apiClient.get('/admin/getAllReservations');

            console.log('Respuesta exitosa:', response.data);
            return response.data;

        } catch (error) {
            console.error('Error al obtener las reservas:', error.response?.data || error.message);

            // Manejo específico de errores
            if (error.response?.status === 403) {
                const errorMsg = error.response.data?.error || 'Acceso denegado';
                console.error('Error 403 detallado:', error.response.data);

                // Verificar si es problema de rol o de registro de admin
                if (errorMsg.includes('permisos de administrador')) {
                    alert('Tu usuario no tiene permisos de administrador. Contacta al SuperAdmin para obtener acceso.');
                } else if (errorMsg.includes('administrador no encontrado')) {
                    alert('Tu usuario no está registrado como administrador en el sistema. Contacta al SuperAdmin.');
                } else {
                    alert(`Error de permisos: ${errorMsg}`);
                }
            } else if (error.response?.status === 401) {
                alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
            } else {
                alert('Error de conexión. Verifica que el servidor esté funcionando.');
            }

            throw error;
        }
    },

    /**
     * Edita una reserva.
     * @param {string} reservationId - ID de la reserva a editar.
     * @param {object} updateData - Datos para actualizar.
     */
    editReservation: async (reservationId, updateData) => {
        try {
            console.log(`Editando reserva ${reservationId}:`, updateData);
            const response = await apiClient.put(`/admin/editReservation/${reservationId}`, updateData);
            console.log('Reserva editada exitosamente:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Error al editar la reserva ${reservationId}:`, error.response?.data || error.message);

            if (error.response?.status === 403) {
                alert('No tienes permisos para editar esta reserva.');
            } else if (error.response?.status === 404) {
                alert('La reserva no fue encontrada.');
            } else {
                alert('Error al editar la reserva. Inténtalo de nuevo.');
            }

            throw error;
        }
    },

    /**
     * Cancela (elimina) una reserva.
     * @param {string} reservationId - ID de la reserva a cancelar.
     */
    cancelReservation: async (reservationId) => {
        try {
            console.log(`Cancelando reserva ${reservationId}...`);
            const response = await apiClient.delete(`/admin/cancelReservation/${reservationId}`);
            console.log('Reserva cancelada exitosamente:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Error al cancelar la reserva ${reservationId}:`, error.response?.data || error.message);

            if (error.response?.status === 403) {
                alert('No tienes permisos para cancelar esta reserva.');
            } else if (error.response?.status === 404) {
                alert('La reserva no fue encontrada.');
            } else {
                alert('Error al cancelar la reserva. Inténtalo de nuevo.');
            }

            throw error;
        }
    },

    // Función auxiliar para verificar el estado de autenticación
    checkAuthStatus: () => {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        const user = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || '{}');

        console.log('Estado de autenticación:', {
            hasToken: !!token,
            tokenPrefix: token ? token.substring(0, 20) + '...' : 'No token',
            userRole: user.role || 'No role',
            userName: user.name || 'No name',
            userEmail: user.email || 'No email'
        });

        return {
            isAuthenticated: !!token,
            user: user,
            isAdmin: ['Admin', 'SuperAdmin'].includes(user.role)
        };
    },

    // ============ GESTIÓN DE VEHÍCULOS (GLOBAL - SUPERADMIN) ============

    /**
     * Obtiene todos los vehículos del sistema.
     * Requiere rol de SuperAdmin.
     */
    getAllVehicles: async () => {
        try {
            const response = await apiClient.get('/admin/getAllVehicles');
            return response.data;
        } catch (error) {
            console.error('Error al obtener todos los vehículos:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Crea un nuevo vehículo en el sistema.
     * Requiere rol de SuperAdmin.
     * @param {object} vehicleData - Datos del vehículo.
     */
    createVehicle: async (vehicleData) => {
        try {
            const response = await apiClient.post('/admin/createVehicle', vehicleData);
            return response.data;
        } catch (error) {
            console.error('Error al crear el vehículo:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Edita un vehículo.
     * @param {object} vehicleData - Datos a actualizar. Debe incluir la placa.
     */
    editVehicle: async (vehicleData) => {
        try {
            // Asumiendo que la ruta de edición en el vehicleController espera el ID (placa) en la URL o cuerpo
            // La ruta actual `/admin/editVehicle` no tiene :plate, así que pasamos los datos en el cuerpo
            const response = await apiClient.put('/admin/editVehicle', vehicleData);
            return response.data;
        } catch (error) {
            console.error(`Error al editar el vehículo ${vehicleData.plate}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Elimina un vehículo del sistema.
     * Requiere rol de SuperAdmin.
     * @param {string} plate - La placa del vehículo a eliminar.
     */
    deleteVehicle: async (plate) => {
        try {
            const response = await apiClient.delete(`/admin/deleteVehicle/${plate}`);
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar el vehículo ${plate}:`, error.response?.data || error.message);
            throw error;
        }
    },

    // ============ REPORTES Y ESTADÍSTICAS (SOLO SUPERADMIN) ============

    /**
     * Obtiene las estadísticas generales del sistema.
     * Requiere rol de SuperAdmin.
     */
    getSystemStats: async () => {
        try {
            const response = await apiClient.get('/admin/getSystemStats');
            return response.data;
        } catch (error) {
            console.error('Error al obtener las estadísticas del sistema:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Obtiene todos los reportes generados.
     * Requiere rol de SuperAdmin.
     */
    getAllReports: async () => {
        try {
            const response = await apiClient.get('/admin/getAllReports');
            return response.data;
        } catch (error) {
            console.error('Error al obtener los reportes:', error.response?.data || error.message);
            throw error;
        }
    },

    // ============ GESTIÓN DE BLACKLIST ============

    /**
     * Obtiene la blacklist de un parqueo específico.
     * @param {string} parkingId - El ID del parqueo.
     */
    getBlacklist: async (parkingId) => {
        try {
            const response = await apiClient.get(`/admin/getBlacklist/${parkingId}`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener la blacklist del parqueo ${parkingId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Agrega una placa a la blacklist de un parqueo.
     * @param {string} id - El ID del parqueo.
     * @param {object} data - El objeto con la placa a agregar. Ej: { plate: 'XYZ-123' }.
     */
    addToBlacklist: async (id, data) => {
        try {
            const response = await apiClient.post(`/admin/addToBlacklist/${id}`, data);
            return response.data;
        } catch (error) {
            console.error(`Error al agregar a la blacklist del parqueo ${id}:`, error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Remueve una placa de la blacklist de un parqueo.
     * @param {string} id - El ID del parqueo.
     * @param {object} data - El objeto con la placa a remover. Ej: { plate: 'XYZ-123' }.
     */
    removeFromBlacklist: async (id, data) => {
        try {
            // El método DELETE de axios permite un body a través del objeto de configuración.
            const response = await apiClient.delete(`/admin/removeFromBlacklist/${id}`, { data });
            return response.data;
        } catch (error) {
            console.error(`Error al remover de la blacklist del parqueo ${id}:`, error.response?.data || error.message);
            throw error;
        }
    },
};

export default adminService;